import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import { AppDialog } from '@/components/app-dialog'
import { SelectField } from '@/components/form'
import {
  chooseAddTags,
  chooseRemoveTags,
  type CouponBulkEditValues,
  type CouponBulkStatusAction,
  EMPTY_COUPON_BULK_EDIT,
  hasCouponBulkChange,
} from '@/domain/coupon-bulk'
import type { AffiliateCouponTagSchema } from '@/domain/coupons'

import { CouponFormError } from './coupon-form-error'
import type { CouponTagsState } from './coupon-shared-fields'
import { CouponTagsField } from './coupon-tags-field'

const STATUS_LABEL_IDS: Record<CouponBulkStatusAction, string> = {
  keep: 'admin.partnerDetail.coupons.bulk.statusKeep',
  activate: 'admin.partnerDetail.coupons.bulk.statusActivate',
  deactivate: 'admin.partnerDetail.coupons.bulk.statusDeactivate',
}

// Narrow the select's raw string to the action union without an `as` cast (`as`
// is banned in app code — see data-table.tsx). Anything unexpected, including
// the empty deselect value, falls back to the no-op action.
const asStatusAction = (value: string): CouponBulkStatusAction =>
  value === 'activate' || value === 'deactivate' ? value : 'keep'

type CouponBulkEditDialogProps = {
  /** How many links the edit applies to — drives the CTA and the header. */
  selectedCount: number
  tagState: CouponTagsState
  /**
   * The tags the selection actually carries — the only ones a removal could do
   * anything to. Offering every partner tag here would let the admin pick tags
   * no selected link has, which the server correctly ignores but which reads as
   * a removal that silently did nothing.
   */
  removableTags: AffiliateCouponTagSchema[]
  submitting: boolean
  /**
   * A rejected bulk write. Rendered INSIDE the dialog: this modal sits above the
   * tab's page-level alert, so an admin who stays here after a failure would
   * otherwise see the Apply button simply re-enable with no explanation.
   */
  serverError: unknown
  onClose: () => void
  onSubmit: (values: CouponBulkEditValues) => void
}

/**
 * "Apply to every selected link" editor for the admin Links table.
 *
 * Deliberately NOT the full `CouponEditDialog` over a selection: `code` is
 * unique per link so there is no single value to apply, and a plan scope
 * reconstructed from a mixed selection is ambiguous. This dialog exposes only
 * the fields that carry an unambiguous meaning across many links — tag deltas
 * (added or removed per link, never a whole-set replace) and the active flag.
 *
 * Mounted only while open (like {@link import('./coupon-edit-dialog')}), so the
 * chosen values start empty on every run without a reset effect — a previous
 * run's choices can never be re-applied to a selection they were not picked for.
 */
export const CouponBulkEditDialog = ({
  selectedCount,
  tagState,
  removableTags,
  submitting,
  serverError,
  onClose,
  onSubmit,
}: CouponBulkEditDialogProps) => {
  const { formatMessage } = useIntl()
  const [values, setValues] = useState<CouponBulkEditValues>(EMPTY_COUPON_BULK_EDIT)
  const onAddTagsChange = (addTagIds: string[]) =>
    setValues((previous) => chooseAddTags(previous, addTagIds))
  const onRemoveTagsChange = (removeTagIds: string[]) =>
    setValues((previous) => chooseRemoveTags(previous, removeTagIds))

  return (
    <AppDialog
      open
      width={480}
      onClose={onClose}
    >
      <AppDialog.Header
        title={formatMessage({ id: 'admin.partnerDetail.coupons.bulk.editTitle' })}
        subtitle={formatMessage(
          { id: 'admin.partnerDetail.coupons.bulk.editSubtitle' },
          { count: selectedCount },
        )}
      />
      <AppDialog.Body>
        <CouponTagsField
          createError={tagState.createTagError}
          createServerError={tagState.createTagServerError}
          creating={tagState.creatingTag}
          deleteError={tagState.deleteTagError}
          deleting={tagState.deletingTag}
          helperText={formatMessage({ id: 'admin.partnerDetail.coupons.bulk.addTagsHelper' })}
          label={formatMessage({ id: 'admin.partnerDetail.coupons.bulk.addTags' })}
          loadError={tagState.tagsError}
          loading={tagState.tagsLoading}
          tags={tagState.tags}
          value={values.addTagIds}
          onChange={onAddTagsChange}
          onCreateTag={tagState.onCreateTag}
          onDeleteTag={tagState.onDeleteTag}
        />

        {removableTags.length > 0 && (
          <CouponTagsField
            allowManage={false}
            createError={tagState.createTagError}
            createServerError={tagState.createTagServerError}
            creating={tagState.creatingTag}
            dataTst="coupon-bulk-remove-tags"
            deleteError={tagState.deleteTagError}
            deleting={tagState.deletingTag}
            helperText={formatMessage({ id: 'admin.partnerDetail.coupons.bulk.removeTagsHelper' })}
            label={formatMessage({ id: 'admin.partnerDetail.coupons.bulk.removeTags' })}
            loadError={tagState.tagsError}
            loading={tagState.tagsLoading}
            tags={removableTags}
            value={values.removeTagIds}
            onChange={onRemoveTagsChange}
            onCreateTag={tagState.onCreateTag}
            onDeleteTag={tagState.onDeleteTag}
          />
        )}

        <SelectField
          label={formatMessage({ id: 'coupons.status' })}
          value={values.status}
          options={Object.entries(STATUS_LABEL_IDS).map(([value, id]) => ({
            value,
            label: formatMessage({ id }),
          }))}
          onValueChange={(status) =>
            setValues((previous) => ({ ...previous, status: asStatusAction(status) }))
          }
        />

        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedMessage
            id="admin.partnerDetail.coupons.bulk.skipNote"
            values={{ count: selectedCount }}
          />
        </Typography>

        <CouponFormError serverError={serverError} />
      </AppDialog.Body>
      <AppDialog.Footer>
        <Button
          disabled={submitting}
          onClick={onClose}
        >
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <Button
          data-tst="coupon-bulk-submit"
          disabled={submitting || !hasCouponBulkChange(values)}
          variant="contained"
          onClick={() => onSubmit(values)}
        >
          <FormattedMessage
            id="admin.partnerDetail.coupons.bulk.apply"
            values={{ count: selectedCount }}
          />
        </Button>
      </AppDialog.Footer>
    </AppDialog>
  )
}
